import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"

/**
 * useTutorSlotMoveLink — le lien du mail « Un changement dans les cours de … »
 * (`/menu/parent?student_id=…&tutor_slot_move=…`) ouvre l'accueil parent sur l'enfant concerné,
 * la feuille « Garder son tuteur » ouverte sur son choix.
 *
 * Les paramètres sont lus une fois puis retirés de la route : la demande vit ici jusqu'à ce que
 * la feuille s'ouvre. La bascule d'enfant est celle de la pastille de profil
 * (`ParentMainLayout.handleUserSelection`), jouée une seule fois par lien : un parent qui revient
 * ensuite sur un autre enfant n'y est pas ramené. L'ouverture, elle, est tenue par l'écran ports
 * (`useStayWithTutorFlow`), qui attend que le choix soit chargé pour l'enfant affiché ; la demande
 * ne lui descend que lorsque cet enfant est celui du lien.
 */
export function useTutorSlotMoveLink({ route, navigation, selectedUser, familyAccounts, handleUserSelection }) {
    const linkStudentId = route?.params?.student_id ?? null
    const linkMoveId = route?.params?.tutor_slot_move ?? null
    const [intent, setIntent] = useState(null)
    // L'entrée dans l'espace relit le compte connecté (`ParentMainLayout`) : une bascule jouée
    // avant que cette lecture ne revienne serait défaite par elle, qui resynchronise l'enfant affiché.
    const userLoading = useSelector((state) => !!state.api.getUser?.request)

    useEffect(() => {
        if (linkMoveId == null) return
        setIntent({ studentId: linkStudentId, moveId: linkMoveId, switched: false })
        navigation?.setParams({ student_id: undefined, tutor_slot_move: undefined })
    }, [linkStudentId, linkMoveId])

    const isLinkedChild = intent != null && (intent.studentId == null || String(intent.studentId) === String(selectedUser?.userId))
    const target =
        intent != null && !intent.switched && !isLinkedChild && selectedUser != null && !userLoading
            ? familyAccounts?.find((account) => String(account?.userId) === String(intent.studentId))
            : null

    useEffect(() => {
        if (!target?.token) return
        setIntent((current) => (current ? { ...current, switched: true } : current))
        handleUserSelection?.(target)
    }, [target?.userId, target?.token])

    const onOpened = useCallback(() => setIntent(null), [])

    return {
        openTutorSlotMoveId: isLinkedChild ? intent.moveId : null,
        onTutorSlotMoveLinkOpened: onOpened,
    }
}
